import React, {Component, Fragment} from "react"

import {connect} from "@irati/store"
import Loading from "@irati/ui/src/Loading"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import id from "@irati/utils/id"
import scheduledTransferActions from "@irati/wallet/store/actions/ScheduledTransfer"
import {ReduxState} from "@irati/store/types"
import DateGroupedScheduledTransfers from "@irati/wallet/types/DateGroupedScheduledTransfers"
import {ScheduledTransferStatus} from "@irati/types"
import {Body} from "@irati/ui/src/Typography"

import NoData, {NoDataCTA} from "../../NoData"
import DateHeader from "../DateHeader"
import {StyledHr, StyledScheduledTransfer, ShowMoreButton, StyledTooltip} from "./styled"

const PAGINATION_COUNT = 10

const PENDING_TRANSACTIONS = [
  ScheduledTransferStatus.NotActivated,
  ScheduledTransferStatus.Pending,
  ScheduledTransferStatus.Failing,
  ScheduledTransferStatus.Failed
]
const CANCELLED_TRANSACTIONS = [ScheduledTransferStatus.Cancelled]

interface WalletScheduledTransfer {
  results: DateGroupedScheduledTransfers[]
}

interface Props extends InjectIntl {
  isCancelledTransfers?: boolean
  className?: string
  listScheduledTransfers: (
    existingScheduledTransfers: DateGroupedScheduledTransfers[],
    walletOwnerId: string,
    walletId: string,
    scheduledTransferStatusParam: ScheduledTransferStatus[],
    cursor: string | null,
    limit: number
  ) => Promise<any>
  clearScheduledTransfers: () => Promise<any>
  wallet: Wallet
  walletScheduledTransfer: WalletScheduledTransfer
  isMobile: boolean
}

class ScheduledTransfers extends Component<Props> {
  static defaultProps = {
    className: null
  }

  state = {
    load: null,
    canLoadMore: true,
    cursor: null
  }

  getMoreScheduledTransfers = (existingScheduledTransfers: DateGroupedScheduledTransfers[], cursor: string | null) => {
    const {ownerUuid, uuid} = this.props.wallet
    const scheduledTransferStatusParam = this.props.isCancelledTransfers ? CANCELLED_TRANSACTIONS : PENDING_TRANSACTIONS
    const list = this.props
      .listScheduledTransfers(existingScheduledTransfers, ownerUuid, uuid, scheduledTransferStatusParam, cursor, PAGINATION_COUNT)
      .then(({newCursor}) => {
        this.setState({
          canLoadMore: true,
          cursor: newCursor
        })
      })
    this.setState({
      load: list,
      canLoadMore: false
    })
  }

  componentDidMount() {
    if (this.props.wallet.uuid) {
      this.getMoreScheduledTransfers([], null)
    }
  }

  componentWillUnmount() {
    this.props.clearScheduledTransfers()
  }

  componentDidUpdate(nextProps: Props) {
    if (this.props.wallet.uuid !== nextProps.wallet.uuid) {
      this.getMoreScheduledTransfers([], null)
    }
  }

  handleShowMoreClick = () => {
    const {cursor} = this.state
    if (cursor) {
      const {results} = this.props.walletScheduledTransfer
      this.getMoreScheduledTransfers(results, cursor)
    }
  }

  render() {
    const {className, walletScheduledTransfer, formatMessage, isMobile, isCancelledTransfers} = this.props
    const {results} = walletScheduledTransfer
    const {cursor, load, canLoadMore} = this.state

    if (!results) {
      return <Loading load={load} />
    }

    const hasScheduledTransfers = results.length > 0

    const idNamespace = id("scheduled-transfers", this.props)

    return (
      <div className={className} id={idNamespace}>
        {hasScheduledTransfers && (
          <Fragment>
            <Body id={id("warning", idNamespace)} small marginBottom={1}>
              {isCancelledTransfers && (
                <>
                  <FormattedMessage id="wallet.cancelledTransfers.tooltip.title" />
                  <StyledTooltip placement="bottom">
                    <FormattedMessage id="wallet.cancelledTransfers.tooltip.body" />
                  </StyledTooltip>
                </>
              )}
              {!isCancelledTransfers && <FormattedMessage id="wallet.scheduledTransfers.clearanceWarning" />}
            </Body>
            {results.map(({date, data}) => {
              const key = date.format("YYYY-MM-DD")
              return (
                <Fragment key={key}>
                  <StyledHr />
                  <DateHeader id={id(`date-header_${key}`, idNamespace)} date={date} />
                  {data.map((scheduledTransfer) => (
                    <StyledScheduledTransfer
                      id={id(`scheduled-transfer_${scheduledTransfer.uuid}`, idNamespace)}
                      scheduledTransfer={scheduledTransfer}
                      key={scheduledTransfer.uuid}
                    />
                  ))}
                </Fragment>
              )
            })}
            <Loading load={load} />
            {canLoadMore && cursor && (
              <ShowMoreButton id={idNamespace} onClick={this.handleShowMoreClick}>
                <FormattedMessage id="wallet.scheduledTransfers.showMoreButton.text" values={{count: PAGINATION_COUNT}} />
              </ShowMoreButton>
            )}
          </Fragment>
        )}
        {!hasScheduledTransfers && (
          <NoData
            useCardStyle={isMobile}
            id={idNamespace}
            text={formatMessage({
              id: isCancelledTransfers ? "wallet.noCancelledTransfers.text" : "wallet.noScheduledTransfers.text"
            })}>
            <NoDataCTA />
          </NoData>
        )}
      </div>
    )
  }
}

const mapStateToProps = ({walletScheduledTransfer, mq}: ReduxState) => {
  return {
    walletScheduledTransfer,
    isMobile: mq.lessThan.large
  }
}

const actions = (dispatch: (action: any) => void) => {
  return {
    listScheduledTransfers: (
      existingScheduledTransfers: DateGroupedScheduledTransfers[],
      walletOwnerId: string,
      walletId: string,
      scheduledTransferStatusParam: ScheduledTransferStatus[],
      cursor: string | null,
      limit: number
    ) =>
      dispatch(
        scheduledTransferActions.list(existingScheduledTransfers, walletOwnerId, walletId, scheduledTransferStatusParam, cursor, limit)
      ),
    clearScheduledTransfers: () => dispatch(scheduledTransferActions.clear())
  }
}

export default injectIntl(connect(mapStateToProps, actions)(ScheduledTransfers))
